import { forwardRef } from 'react';
import { ClientProfitability } from '@/services/financeClients';

type Props = {
  clients: ClientProfitability[];
  totals: { revenue: number; cost: number; profit: number; margin: number };
  periodStart: string;
  periodEnd: string;
};

const money = (n: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

const longDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Print-only view of Client Profitability, rendered off-screen and handed to
 * react-to-print (already a dependency, used by the generic ExportButton).
 *
 * Deliberately NOT the on-screen page: that carries period toggles, edit icons,
 * "Add Client" and warning banners, none of which belong in a document shared
 * with a team. This is a plain document — masthead, totals, one row per client,
 * member detail underneath.
 *
 * The basis statement is printed prominently. Revenue here is estimated from
 * contracted rates, so a PDF that circulates without saying so would be read as
 * billed revenue and would not reconcile to QuickBooks.
 */
const FinanceXClientsPrint = forwardRef<HTMLDivElement, Props>(function FinanceXClientsPrint(
  { clients, totals, periodStart, periodEnd },
  ref
) {
  const ranked = [...clients].sort((a, b) => b.revenue - a.revenue);
  const generated = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div ref={ref} className="print-only-report">
      <style>{`
        .print-only-report { display: none; }
        @media print {
          .print-only-report { display: block; padding: 24px; color: #09090B;
            font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
          .print-only-report table { width: 100%; border-collapse: collapse; }
          .print-only-report thead { display: table-header-group; }
          .print-only-report .client-block { page-break-inside: avoid; }
          @page { size: A4 landscape; margin: 12mm; }
        }
      `}</style>

      <div style={{ borderBottom: '2px solid #09090B', paddingBottom: 8, marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Client Profitability</h1>
        <div style={{ fontSize: 12, color: '#4A4B4E', marginTop: 4 }}>
          {longDate(periodStart)} – {longDate(periodEnd)} · generated {generated}
        </div>
      </div>

      {/* Basis first, not in a footnote. */}
      <div
        style={{
          border: '1px solid #BFDBFE',
          background: '#EFF6FF',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 11,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        <strong>Estimated figures.</strong> Revenue is calculated from contracted client rates
        (hourly rates assume a 160-hour month), prorated by each person&apos;s days on the team.
        Cost is monthly salary × allocation, prorated the same way. These are not invoiced amounts
        and will not reconcile to QuickBooks.
      </div>

      <table style={{ fontSize: 12, marginBottom: 20 }}>
        <thead>
          <tr style={{ borderBottom: '1.5px solid #09090B' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Client</th>
            <th style={{ textAlign: 'center', padding: '6px 8px' }}>People</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Est. Revenue</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Allocated Cost</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Gross Profit</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Margin</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Share</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((c) => (
            <tr key={c.teamId} style={{ borderBottom: '1px solid #E4E4E7' }}>
              <td style={{ padding: '5px 8px', fontWeight: 500 }}>{c.teamName}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center' }}>{c.members.length}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{money(c.revenue)}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{money(c.allocatedCost)}</td>
              <td
                style={{
                  padding: '5px 8px',
                  textAlign: 'right',
                  fontWeight: 500,
                  color: c.profit >= 0 ? '#09090B' : '#B91C1C',
                }}
              >
                {money(c.profit)}
              </td>
              <td
                style={{
                  padding: '5px 8px',
                  textAlign: 'right',
                  color: c.margin >= 0 ? '#09090B' : '#B91C1C',
                }}
              >
                {c.margin.toFixed(1)}%
              </td>
              <td style={{ padding: '5px 8px', textAlign: 'right', color: '#71717A' }}>
                {totals.revenue > 0 ? Math.round((c.revenue / totals.revenue) * 100) : 0}%
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '1.5px solid #09090B', fontWeight: 700 }}>
            <td style={{ padding: '7px 8px' }}>Total</td>
            <td style={{ padding: '7px 8px', textAlign: 'center' }}>
              {ranked.reduce((s, c) => s + c.members.length, 0)}
            </td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>{money(totals.revenue)}</td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>{money(totals.cost)}</td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>{money(totals.profit)}</td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>{totals.margin.toFixed(1)}%</td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>100%</td>
          </tr>
        </tbody>
      </table>

      <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 2px' }}>Team detail</h2>
      {/* These rows are a full-month run-rate, not prorated and not reduced by
          allocation — matching the on-screen member table. The client totals
          above ARE prorated, so the two will differ for anyone who joined
          mid-period. Saying so prevents that reading as an error. */}
      <div style={{ fontSize: 10, color: '#71717A', margin: '0 0 8px' }}>
        Monthly run-rate at full allocation. The client totals above are prorated by days on the
        team, so they will differ for anyone who joined part-way through the period.
      </div>
      {ranked.map((c) => (
        <div key={c.teamId} className="client-block" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 3 }}>
            {c.teamName}
            <span style={{ fontWeight: 400, color: '#71717A' }}>
              {' '}
              — {money(c.revenue)} revenue · {money(c.allocatedCost)} cost · {c.margin.toFixed(1)}%
            </span>
          </div>
          {c.members.length === 0 ? (
            <div style={{ fontSize: 11, color: '#71717A', paddingLeft: 8 }}>
              No team members allocated.
            </div>
          ) : (
            <table style={{ fontSize: 11 }}>
              <thead>
                <tr style={{ color: '#71717A', borderBottom: '1px solid #E4E4E7' }}>
                  <th style={{ textAlign: 'left', padding: '3px 8px', fontWeight: 500 }}>Name</th>
                  <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 500 }}>
                    Client Rate
                  </th>
                  <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 500 }}>
                    Mo. Revenue
                  </th>
                  <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 500 }}>
                    Mo. Salary
                  </th>
                  <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 500 }}>
                    Mo. Profit
                  </th>
                  <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 500 }}>Margin</th>
                </tr>
              </thead>
              <tbody>
                {c.members.map((m) => {
                  // Same arithmetic as the on-screen member table, deliberately:
                  // a full month at the client rate, allocation NOT applied, so
                  // the PDF and the screen never disagree.
                  let moRevenue: number | null = null;
                  if (m.clientRate != null) {
                    moRevenue = m.clientRateType === 'hourly' ? m.clientRate * 160 : m.clientRate;
                  }
                  const moSalary = m.monthlyRate;
                  const moProfit =
                    moRevenue != null && moSalary != null ? moRevenue - moSalary : null;
                  const margin =
                    moRevenue != null && moRevenue > 0 && moProfit != null
                      ? Math.round((moProfit / moRevenue) * 100)
                      : null;
                  return (
                    <tr key={m.id}>
                      <td style={{ padding: '3px 8px' }}>
                        {m.firstName} {m.lastName}
                      </td>
                      <td style={{ padding: '3px 8px', textAlign: 'right' }}>
                        {m.clientRate != null
                          ? `$${m.clientRate}${m.clientRateType === 'hourly' ? '/hr' : '/mo'}`
                          : '—'}
                      </td>
                      <td style={{ padding: '3px 8px', textAlign: 'right' }}>
                        {moRevenue != null ? money(moRevenue) : '—'}
                      </td>
                      <td style={{ padding: '3px 8px', textAlign: 'right' }}>
                        {moSalary != null ? money(moSalary) : '—'}
                      </td>
                      <td
                        style={{
                          padding: '3px 8px',
                          textAlign: 'right',
                          fontWeight: 500,
                          color: moProfit != null && moProfit < 0 ? '#B91C1C' : '#09090B',
                        }}
                      >
                        {moProfit != null ? money(moProfit) : '—'}
                      </td>
                      <td
                        style={{
                          padding: '3px 8px',
                          textAlign: 'right',
                          color: margin != null && margin < 0 ? '#B91C1C' : '#09090B',
                        }}
                      >
                        {margin != null ? `${margin}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
});

export default FinanceXClientsPrint;
